import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { einsatzOeffnen, personalToken, personalTokenMerken, personalAbmelden } from '../lib/personalEinsatz';

const ROLLEN_LABELS = {
  turnierleitung: '🏆 Turnierleitung',
  anmeldung: '📋 Anmeldung',
  ombudsmann: '🤝 Ombudsmann',
  hauptkampfrichter: '👨‍⚖️ Hauptkampfrichter',
  seitenkampfrichter: '⚖️ Seitenkampfrichter',
  punktrichter: '📝 Punktrichter',
  zeitnehmer: '⏱️ Zeitnehmer',
  mattenleiter: '🎯 Mattenleiter',
  schreiber: '✍️ Schreiber',
  helfer: '🙌 Helfer',
  arzt: '👨‍⚕️ Arzt',
  sanitaeter: '🚑 Sanitäter',
  kampfrichter: '⚖️ Kampfrichter',
  waage: '⚖ Waage',
  registrierung: '📝 Registrierung',
  aufruf: '📣 Aufruf',
};

// Zugang zu einer eigenen Seite (Server: POST /api/personal/einsatz-zugang)
const EINSATZ_SEITEN = [
  { rolle: 'kampfrichter', titel: 'Kampfrichter-App', sub: (z) => (z.matte_nummer ? `Matte ${z.matte_nummer}` : 'Matte nach dem Öffnen wählen') },
  { rolle: 'waage', titel: 'Waage', sub: () => 'Gewicht eintragen' },
  { rolle: 'registrierung', titel: 'Check-in', sub: () => 'Empfang und Nachmeldung' },
];

function decodeToken(token) {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
}

// Was das Turnierpersonal am Turniertag öffnen darf (18.10.2026). Vorher
// stand hier nur "Eingeloggt — guten Einsatz!" ohne einen einzigen Link.
// Je Rolle die passende Seite: Kampfrichter-App, Waage und Check-in über
// einen Einsatz-Zugang (lib/personalEinsatz.js), die Leitstelle für die
// Turnierleitung, Aufruf und Ansage für alle, die eingeteilt sind
// (Server: services/personalRechte.js). Dazu die öffentlichen Hallenschirme.
function EinsatzKarte({ einsatz }) {
  const tid = einsatz.turnier_id;
  const navigate = useNavigate();
  const [matten, setMatten] = useState([]);
  const [fehler, setFehler] = useState('');
  const [oeffnet, setOeffnet] = useState('');

  useEffect(() => {
    let ab = false;
    fetch(`/api/kampfflaechen/public/${tid}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.message || `Fehler ${r.status}`);
        if (!ab) setMatten(d.data || []);
      })
      .catch((e) => { if (!ab) setFehler(`Matten nicht ladbar: ${e.message}`); });
    return () => { ab = true; };
  }, [tid]);

  const oeffnen = async (rolle) => {
    setOeffnet(rolle); setFehler('');
    try {
      navigate(await einsatzOeffnen(tid, rolle));
    } catch (e) {
      setFehler(`${e.message === 'Failed to fetch' ? 'Keine Verbindung zum Server.' : e.message}`);
    } finally {
      setOeffnet('');
    }
  };

  const datum = einsatz.datum ? new Date(einsatz.datum).toLocaleDateString('de-DE') : 'ohne Termin';
  const link = (href, titel, sub, neu = true) => (
    <a key={href} href={href} target={neu ? '_blank' : undefined} rel="noreferrer" style={s.link}>
      <span style={s.linkTitel}>{titel}</span>
      {sub && <span style={s.linkSub}>{sub}</span>}
    </a>
  );

  return (
    <div style={s.einsatz}>
      <div style={s.einsatzKopf}>
        <div style={s.einsatzName}>{einsatz.turnier_name}</div>
        <div style={s.einsatzSub}>{datum} · {(einsatz.zuweisungen || [einsatz]).map((z) => (ROLLEN_LABELS[z.rolle] || z.rolle)
          + (z.matte_nummer ? ` (Matte ${z.matte_nummer})` : '')).join(' · ')}</div>
      </div>
      <div style={s.linkGitter}>
        {EINSATZ_SEITEN.filter((e) => (einsatz.rollen || []).includes(e.rolle)).map((e) => {
          const passend = (einsatz.zuweisungen || []).filter((x) => (x.zugang || x.rolle) === e.rolle);
          const z = passend.find((x) => x.matte_nummer) || passend[0] || {};
          return (
            <button key={e.rolle} type="button" style={{ ...s.link, ...s.linkHaupt }} disabled={!!oeffnet}
              onClick={() => oeffnen(e.rolle)}>
              <span style={s.linkTitel}>{oeffnet === e.rolle ? 'Wird geöffnet…' : e.titel}</span>
              <span style={s.linkSub}>{e.sub(z)}</span>
            </button>
          );
        })}
        {(einsatz.rollen || []).includes('turnierleitung')
          && link(`/leitstelle/${tid}`, 'Leitstelle', 'Alle Matten, Verzug, Tablets und Fehler auf einen Blick', false)}
        {link(`/aufruf-control/${tid}`, 'Aufruf steuern', 'Kategorien an die Matten rufen, Hallen-Infos', false)}
        {link(`/ansage/${tid}`, 'Ansage-Station', 'Gesprochene Aufrufe für die Halle')}
        {link(`/aufruf/${tid}`, 'Hallenschirm', 'Aufrufe für alle Matten')}
        {link(`/live/${tid}`, 'Ergebnisse live', 'Öffentliche Ergebnisliste')}
      </div>
      <div style={s.mattenTitel}>Mattenschirme</div>
      {fehler && <div style={s.fehler}>{fehler}</div>}
      {!fehler && matten.length === 0 && <div style={s.leise}>Für dieses Turnier sind keine Matten angelegt.</div>}
      <div style={s.mattenGitter}>
        {matten.map((m) => {
          const eigene = (einsatz.zuweisungen || []).some((z) => z.kampfflaeche_id && Number(z.kampfflaeche_id) === Number(m.kampfflaeche_id));
          return (
            <a key={m.kampfflaeche_id} href={`/display/${m.nummer}?t=${tid}`} target="_blank" rel="noreferrer"
              style={{ ...s.matte, ...(eigene ? s.matteEigen : {}) }} title={eigene ? 'Ihre Matte' : undefined}>
              <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>{m.nummer}</span>
              <span style={s.linkSub}>{m.name}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default function StaffDashboard() {
  const navigate = useNavigate();
  const [staffInfo, setStaffInfo] = useState(null);
  const [einsaetze, setEinsaetze] = useState(null);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    // Nach Waage/Check-in liegt unter 'token' der Einsatz-Zugang — das eigene
    // Konto steht unter 'personalToken' (lib/personalEinsatz.js).
    const token = personalToken();
    if (!token) { navigate('/anmelden'); return; }
    const payload = decodeToken(token);
    if (!payload || payload.role !== 'personal' || payload.einsatz) { navigate('/anmelden'); return; }
    if (payload.exp && payload.exp * 1000 < Date.now()) { personalAbmelden(); navigate('/anmelden'); return; }
    personalTokenMerken(token);
    localStorage.setItem('token', token);
    setStaffInfo(payload);
  }, [navigate]);

  useEffect(() => {
    if (!staffInfo) return;
    fetch('/api/personal/mein-einsatz', { headers: { Authorization: `Bearer ${personalToken() || ''}` } })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
        // Je Turnier eine Karte, auch bei mehreren Rollen im selben Turnier
        const jeTurnier = new Map();
        // Rollen je Turnier sammeln: die Leitstelle gibt es nur für die Turnierleitung.
        for (const z of d.data || []) {
          if (!jeTurnier.has(z.turnier_id)) jeTurnier.set(z.turnier_id, { ...z, rollen: [], zuweisungen: [] });
          // `zugang` (Server): Hauptkampfrichter & Co. öffnen die Kampfrichter-App,
          // Anmeldung den Check-in (services/personalRechte.js)
          jeTurnier.get(z.turnier_id).rollen.push(z.zugang || z.rolle);
          jeTurnier.get(z.turnier_id).zuweisungen.push(z);
        }
        setEinsaetze([...jeTurnier.values()]);
      })
      .catch((e) => { setEinsaetze([]); setFehler(`Einsatz nicht ladbar: ${e.message}`); });
  }, [staffInfo]);

  const handleLogout = () => {
    personalAbmelden();
    navigate('/anmelden');
  };

  if (!staffInfo) return null;

  const rollLabel = ROLLEN_LABELS[staffInfo.primaer_rolle] || staffInfo.primaer_rolle;

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <span style={s.icon}>👥</span>
          <div>
            <div style={s.name}>{staffInfo.name}</div>
            <div style={s.rolle}>{rollLabel}</div>
          </div>
        </div>

        <div style={s.infoBox}>
          <div style={s.infoRow}>
            <span style={s.infoLabel}>Benutzername</span>
            <span style={s.infoVal}>{staffInfo.username}</span>
          </div>
          <div style={s.infoRow}>
            <span style={s.infoLabel}>Rolle</span>
            <span style={s.infoVal}>{rollLabel}</span>
          </div>
        </div>

        <div style={s.statusBox}>
          <span style={s.statusDot} />
          Eingeloggt — guten Einsatz!
        </div>

        {einsaetze === null && <div style={s.leise}>Einsatz wird geladen…</div>}
        {fehler && <div style={s.fehler} role="alert">{fehler}</div>}
        {einsaetze && einsaetze.length === 0 && !fehler && (
          <div style={s.leise}>Sie sind noch keinem Turnier zugewiesen. Bitte bei der Turnierleitung melden.</div>
        )}
        {einsaetze && einsaetze.map((e) => <EinsatzKarte key={e.turnier_id} einsatz={e} />)}

        <button style={s.logoutBtn} onClick={handleLogout}>
          Abmelden
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#0d0d0d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    width: '100%',
    maxWidth: 640,
    background: '#161616',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '2rem',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  icon: { fontSize: '2.5rem', lineHeight: 1 },
  name: { fontSize: '1.3rem', fontWeight: 700, color: '#fff' },
  rolle: { fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.2rem' },
  infoBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10,
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  infoLabel: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.04em' },
  infoVal: { fontSize: '0.9rem', color: '#fff', fontWeight: 500 },
  statusBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#22c55e',
    fontSize: '0.9rem',
    fontWeight: 500,
  },
  statusDot: {
    width: 8, height: 8,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 6px #22c55e',
    flexShrink: 0,
  },
  leise: { fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' },
  fehler: { fontSize: '0.85rem', color: '#fca5a5', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 8, padding: '0.5rem 0.75rem' },
  einsatz: { border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  einsatzKopf: {},
  einsatzName: { fontSize: '1.05rem', fontWeight: 700, color: '#fff' },
  einsatzSub: { fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.15rem' },
  linkGitter: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '0.5rem' },
  link: { display: 'flex', flexDirection: 'column', gap: '0.2rem', padding: '0.7rem 0.8rem', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', textDecoration: 'none', textAlign: 'left', font: 'inherit', cursor: 'pointer' },
  linkHaupt: { background: 'rgba(220,38,38,0.14)', borderColor: 'rgba(220,38,38,0.55)' },
  linkTitel: { fontWeight: 600, fontSize: '0.9rem' },
  linkSub: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)' },
  mattenTitel: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  mattenGitter: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '0.4rem' },
  matte: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem', borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', textDecoration: 'none' },
  matteEigen: { borderColor: '#d9aa43', background: 'rgba(217,170,67,0.12)' },
  logoutBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: 'rgba(255,255,255,0.5)',
    padding: '0.6rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
};
